import { useEffect, useState } from "react";
import { Save, Upload, Trash2, Plus, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/appApi";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

export default function Settings() {
  const [s, setS] = useState(null);
  const [uploading, setUploading] = useState("");

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Akses ditolak")); }, []);
  if (!s) return <p className="text-gray-500">Memuat…</p>;

  const set = (k, v) => setS((o) => ({ ...o, [k]: v }));
  const setSocial = (k, v) => setS((o) => ({ ...o, social_media: { ...o.social_media, [k]: v } }));

  const save = async () => { try { await api.put("/settings", s); toast.success("Pengaturan disimpan"); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } };

  const uploadLogo = async (file, field) => {
    setUploading(field);
    const fd = new FormData(); fd.append("file", file); fd.append("purpose", "logo");
    try { const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); await api.put("/settings", { [field]: r.data.url }); set(field, r.data.url); toast.success("Logo diunggah"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setUploading(""); }
  };

  const purgeDemo = async () => {
    if (!window.confirm("Hapus semua konten DEMO dan matikan mode demo? Konfigurasi organisasi tetap aman.")) return;
    try { const r = await api.delete("/demo"); toast.success(`${r.data.deleted} konten demo dihapus`); set("demo_mode", false); } catch { toast.error("Gagal"); }
  };

  const officials = s.officials || [];
  const docs = s.reference_documents || [];

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl font-extrabold text-dlh-green">Pengaturan</h1>
        <Button onClick={save} data-testid="settings-save" className="bg-dlh-green hover:bg-dlh-greenDark"><Save className="h-4 w-4 mr-1" /> Simpan</Button>
      </div>

      <Section title="Identitas Portal">
        <div className="grid sm:grid-cols-2 gap-4">
          <LogoUpload label="Logo Portal PAPA BERSERI" value={s.logo_portal_url} onUpload={(f) => uploadLogo(f, "logo_portal_url")} loading={uploading === "logo_portal_url"} />
          <LogoUpload label="Logo PEMDA Mimika (resmi)" value={s.logo_pemda_url} onUpload={(f) => uploadLogo(f, "logo_pemda_url")} loading={uploading === "logo_pemda_url"} />
        </div>
        <p className="text-xs text-gray-400 mt-2">Unggah lambang resmi yang disediakan pengelola. Jangan menggunakan lambang palsu.</p>
        <Toggle label="Status Portal Resmi" hint="Aktifkan hanya setelah identitas & pengelola dikonfirmasi pemilik." checked={s.official_portal_status} onChange={(v) => set("official_portal_status", v)} testid="toggle-official" />
      </Section>

      <Section title="Mode Demo">
        <Toggle label="Mode Demo Aktif" hint="Konten DEMO hanya tampil pada mode demo dan tidak diindeks mesin pencari." checked={s.demo_mode} onChange={(v) => set("demo_mode", v)} testid="toggle-demo" />
        <button onClick={purgeDemo} data-testid="purge-demo-btn" className="mt-3 inline-flex items-center gap-2 text-sm text-red-600 border border-red-300 rounded-lg px-4 py-2 hover:bg-red-50">
          <AlertTriangle className="h-4 w-4" /> Hapus Semua Konten Demo
        </button>
      </Section>

      <Section title="Visi & Misi">
        <Text label="Visi" value={s.visi} onChange={(v) => set("visi", v)} />
        <Area label="Misi" value={s.misi} onChange={(v) => set("misi", v)} />
        <p className="text-xs text-gray-400">Ditampilkan publik hanya setelah diisi dan diverifikasi.</p>
      </Section>

      <Section title="Kontak">
        <Text label="Alamat" value={s.address} onChange={(v) => set("address", v)} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Text label="Email" value={s.email} onChange={(v) => set("email", v)} />
          <Text label="Telepon" value={s.phone} onChange={(v) => set("phone", v)} />
          <Text label="Nomor Layanan" value={s.service_number} onChange={(v) => set("service_number", v)} />
          <Text label="Jam Layanan" value={s.service_hours} onChange={(v) => set("service_hours", v)} />
          <Text label="URL Kanal Pengaduan Resmi" value={s.complaint_channel_url} onChange={(v) => set("complaint_channel_url", v)} />
        </div>
      </Section>

      <Section title="Media Sosial">
        <div className="grid sm:grid-cols-2 gap-4">
          {["facebook", "instagram", "youtube", "whatsapp"].map((k) => (
            <Text key={k} label={k[0].toUpperCase() + k.slice(1)} value={s.social_media?.[k]} onChange={(v) => setSocial(k, v)} />
          ))}
        </div>
      </Section>

      <Section title="Pengelola / Pejabat (terverifikasi)">
        {officials.map((o, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input value={o.name} onChange={(e) => set("officials", officials.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} placeholder="Nama" className="flex-1 border rounded-lg px-3 py-2 text-sm" />
            <input value={o.position} onChange={(e) => set("officials", officials.map((x, idx) => idx === i ? { ...x, position: e.target.value } : x))} placeholder="Jabatan" className="flex-1 border rounded-lg px-3 py-2 text-sm" />
            <button onClick={() => set("officials", officials.filter((_, idx) => idx !== i))} className="p-2 text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button onClick={() => set("officials", [...officials, { name: "", position: "" }])} className="text-sm text-dlh-green flex items-center gap-1"><Plus className="h-4 w-4" /> Tambah</button>
      </Section>

      <Section title="Dokumen Rujukan">
        {docs.map((d, i) => (
          <div key={i} className="grid sm:grid-cols-3 gap-2 mb-2">
            <input value={d.title} onChange={(e) => set("reference_documents", docs.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))} placeholder="Judul" className="border rounded-lg px-3 py-2 text-sm" />
            <input value={d.note} onChange={(e) => set("reference_documents", docs.map((x, idx) => idx === i ? { ...x, note: e.target.value } : x))} placeholder="Keterangan" className="border rounded-lg px-3 py-2 text-sm" />
            <div className="flex gap-2">
              <input value={d.url} onChange={(e) => set("reference_documents", docs.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x))} placeholder="URL (opsional)" className="flex-1 border rounded-lg px-3 py-2 text-sm" />
              <button onClick={() => set("reference_documents", docs.filter((_, idx) => idx !== i))} className="p-2 text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        <button onClick={() => set("reference_documents", [...docs, { title: "", note: "", url: "" }])} className="text-sm text-dlh-green flex items-center gap-1"><Plus className="h-4 w-4" /> Tambah</button>
      </Section>
    </div>
  );
}

function Section({ title, children }) { return <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5"><h2 className="font-serif font-bold text-dlh-green mb-4">{title}</h2>{children}</div>; }
function Text({ label, value, onChange }) { return <div className="mb-3"><label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label><input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>; }
function Area({ label, value, onChange }) { return <div className="mb-3"><label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label><textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>; }
function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 mt-4">
      <div><p className="text-sm font-semibold text-gray-700">{label}</p><p className="text-xs text-gray-400">{hint}</p></div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
function LogoUpload({ label, value, onUpload, loading }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      <label className="flex items-center gap-3 border-2 border-dashed border-gray-300 rounded-lg p-3 cursor-pointer hover:border-dlh-green">
        {value ? <img src={mediaUrl(value)} alt="" className="h-12 w-12 rounded object-cover" /> : loading ? <Loader2 className="h-6 w-6 animate-spin text-dlh-green" /> : <Upload className="h-6 w-6 text-gray-400" />}
        <span className="text-xs text-gray-500">{value ? "Ganti logo" : "Unggah logo"}</span>
        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
      </label>
    </div>
  );
}
